import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { LIBELLE_ROLE, type Role } from '@gsm/partage';
import { useAuth } from '../auth/contexte';
import { Icone, type NomIcone } from './Icone';

type Entree = { libelle: string; icone: NomIcone; chemin: string; roles?: Role[] };

/**
 * Une entrée par module métier. Sans rôle déclaré, l'entrée est visible de
 * tous. Une section se divise en groupes quand elle en porte : chacun a son
 * sous-titre, et la section entière disparaît si aucun n'a d'entrée visible.
 */
const MODULES: {
  section: string;
  groupes: { titre?: string; entrees: Entree[] }[];
}[] = [
  {
    section: 'Référentiels',
    groupes: [
      {
        titre: 'Clientèle',
        entrees: [
          { libelle: 'Agences', icone: 'agences', chemin: '/referentiels/agences' },
          { libelle: 'Clients', icone: 'clients', chemin: '/clients' },
          { libelle: 'Mandats', icone: 'mandats', chemin: '/mandats' },
          { libelle: 'Profils', icone: 'profils', chemin: '/referentiels/profils' },
        ],
      },
      {
        titre: 'Marché',
        entrees: [
          { libelle: 'Instruments', icone: 'instruments', chemin: '/referentiels/instruments' },
          { libelle: 'Indices de référence', icone: 'indices', chemin: '/referentiels/indices' },
          { libelle: 'Tranches de maturité', icone: 'tranches', chemin: '/referentiels/tranches' },
        ],
      },
    ],
  },
  {
    section: 'Gestion',
    groupes: [
      {
        entrees: [
          {
            libelle: 'À investir',
            icone: 'investir',
            chemin: '/tresorerie?oisifs=1',
            // L'ADMIN est superutilisateur (décision du 04/08/2026) et voit déjà la
            // trésorerie : lui cacher le filtre qui la restreint n'aurait pas de sens.
            roles: ['GERANT', 'RESP_GESTION', 'ADMIN'],
          },
          {
            libelle: 'Sorties de fonds',
            icone: 'sorties',
            chemin: '/sorties-fonds',
            roles: ['GERANT', 'RESP_GESTION', 'DIRECTION', 'CONFORMITE', 'ADMIN'],
          },
          {
            libelle: 'Campagnes',
            icone: 'campagnes',
            chemin: '/campagnes',
            roles: ['GERANT', 'RESP_GESTION', 'DIRECTION', 'CONFORMITE', 'ADMIN'],
          },
          {
            libelle: 'Trésorerie',
            icone: 'tresorerie',
            chemin: '/tresorerie',
            roles: ['GERANT', 'RESP_GESTION', 'MIDDLE_BO', 'DIRECTION', 'ADMIN'],
          },
          {
            libelle: 'Allocations cibles',
            icone: 'allocations',
            chemin: '/allocations',
            roles: ['GERANT', 'RESP_GESTION', 'DIRECTION', 'CONFORMITE', 'ADMIN'],
          },
        ],
      },
    ],
  },
  {
    section: 'Ordres',
    groupes: [
      {
        entrees: [
          {
            libelle: 'Ordres',
            icone: 'ordres',
            chemin: '/ordres',
            roles: ['GERANT', 'RESP_GESTION', 'MIDDLE_BO'],
          },
          {
            libelle: 'Paniers',
            icone: 'paniers',
            chemin: '/paniers',
            roles: ['MIDDLE_BO', 'RESP_GESTION'],
          },
        ],
      },
    ],
  },
  {
    // Ces deux écrans ne produisent rien : ils reçoivent. La situation du
    // back-office tiers, les cours du marché. Le portefeuille qui en découle se
    // consulte depuis la fiche du mandat.
    section: 'Réception',
    groupes: [
      {
        entrees: [
          {
            libelle: 'Situation reçue',
            icone: 'situation',
            chemin: '/import-situation',
            roles: ['MIDDLE_BO', 'RESP_GESTION'],
          },
          {
            libelle: 'Cours du jour',
            icone: 'import',
            chemin: '/import-cours',
            roles: ['MIDDLE_BO', 'RESP_GESTION'],
          },
        ],
      },
    ],
  },
  {
    section: 'Restitution',
    groupes: [
      {
        entrees: [
          {
            libelle: 'Reporting',
            icone: 'reporting',
            chemin: '/reporting',
            roles: ['MIDDLE_BO', 'GERANT', 'RESP_GESTION', 'CONFORMITE', 'DIRECTION', 'ADMIN'],
          },
        ],
      },
    ],
  },
  {
    section: 'Contrôle',
    groupes: [
      {
        entrees: [
          {
            libelle: 'Journal d’audit',
            icone: 'audit',
            chemin: '/journal-audit',
            roles: ['CONFORMITE', 'ADMIN', 'DIRECTION'],
          },
          {
            libelle: 'Contrôle de cohérence',
            icone: 'controle',
            chemin: '/controle-coherence',
            roles: ['CONFORMITE', 'DIRECTION', 'ADMIN'],
          },
        ],
      },
    ],
  },
];

/**
 * Rôles qui atteignent un écran depuis le rail, ou `undefined` s'il est ouvert
 * à tous. L'entrée exacte (requête comprise) prime sur celle du seul chemin :
 * « À investir » n'a pas les rôles de « Trésorerie ».
 */
export function rolesDuChemin(chemin: string): Role[] | undefined {
  const entrees = MODULES.flatMap((m) => m.groupes.flatMap((g) => g.entrees));
  const base = chemin.split('?')[0];
  const entree = entrees.find((e) => e.chemin === chemin) ?? entrees.find((e) => e.chemin === base);
  return entree?.roles;
}

/**
 * Une entrée est active quand l'écran affiché est le sien, **filtre compris**.
 *
 * `NavLink` ignore la partie requête : sans cette comparaison, « À investir »
 * et « Trésorerie » — qui visent le même écran, l'un filtré, l'autre non —
 * s'allumeraient ensemble.
 */
export function estActif(chemin: string, pathname: string, search: string): boolean {
  const [base, requete = ''] = chemin.split('?');
  if (!base || !pathname.startsWith(base)) return false;
  return requete === search.replace(/^\?/, '');
}

const CLE_REPLIE = 'gsm.rail.replie';
const CLE_OUVERTES = 'gsm.rail.sectionsOuvertes';

/**
 * Préférences d'affichage du rail, propres au poste. Le stockage peut manquer
 * (navigation privée, données bloquées) : le rail retombe alors sur son état
 * par défaut — déplié, toutes sections fermées — sans jamais échouer.
 */
function lirePreference<T>(cle: string, defaut: T): T {
  try {
    const brut = localStorage.getItem(cle);
    return brut === null ? defaut : (JSON.parse(brut) as T);
  } catch {
    return defaut;
  }
}

function ecrirePreference(cle: string, valeur: unknown): void {
  try {
    localStorage.setItem(cle, JSON.stringify(valeur));
  } catch {
    // Préférence perdue au rechargement : sans gravité.
  }
}

/** Section qui porte l'écran affiché, s'il en a une. */
export function sectionActive(pathname: string, search: string): string | undefined {
  return MODULES.find((module) =>
    module.groupes.some((groupe) =>
      groupe.entrees.some((entree) => estActif(entree.chemin, pathname, search)),
    ),
  )?.section;
}

export function Charpente() {
  const { profil, deconnexion, aRole } = useAuth();
  const { pathname, search } = useLocation();
  const [replie, setReplie] = useState(() => lirePreference(CLE_REPLIE, false));
  // Toutes les sections sont pliées par défaut : seules celles que
  // l'utilisateur a ouvertes sont retenues.
  const [ouvertes, setOuvertes] = useState<string[]>(() => lirePreference(CLE_OUVERTES, []));

  useEffect(() => ecrirePreference(CLE_REPLIE, replie), [replie]);
  useEffect(() => ecrirePreference(CLE_OUVERTES, ouvertes), [ouvertes]);

  // L'écran affiché ne doit jamais se trouver dans une section fermée : on
  // perdrait le repère de l'entrée active. Naviguer vers elle la rouvre.
  const active = sectionActive(pathname, search);
  useEffect(() => {
    if (active) setOuvertes((o) => (o.includes(active) ? o : [...o, active]));
  }, [active]);

  const basculerSection = (section: string) =>
    setOuvertes((o) => (o.includes(section) ? o.filter((s) => s !== section) : [...o, section]));

  // Rail replié : seules les icônes restent, le libellé passe en infobulle et
  // demeure le nom accessible du lien.
  const lien = (chemin: string, icone: NomIcone, libelle: string, actif: boolean, fin = false) => (
    <NavLink
      key={libelle}
      to={chemin}
      end={fin}
      className={actif ? 'actif' : ''}
      title={replie ? libelle : undefined}
    >
      <span className="rail__item">
        <Icone nom={icone} />
        <span className="rail__libelle">{libelle}</span>
      </span>
    </NavLink>
  );

  return (
    <>
      <header className="bandeau">
        <div className="bandeau__marque">
          GSM<span>.</span>
        </div>
        <div className="bandeau__util">
          <b>
            {profil?.prenom} {profil?.nom}
          </b>
          <em>{profil?.roles.map((r) => LIBELLE_ROLE[r as Role]).join(' · ')}</em>
        </div>
        <button className="bandeau__quitter" onClick={() => void deconnexion()}>
          <Icone nom="quitter" taille={15} />
          Quitter
        </button>
      </header>

      <div className={`charpente${replie ? ' charpente--repliee' : ''}`}>
        <nav className="rail" aria-label="Navigation principale">
          <button
            type="button"
            className="rail__bascule"
            aria-expanded={!replie}
            title={replie ? 'Déplier le menu' : undefined}
            onClick={() => setReplie((r) => !r)}
          >
            <Icone nom={replie ? 'deplier' : 'replier'} />
            <span className="rail__libelle">{replie ? 'Déplier le menu' : 'Replier le menu'}</span>
          </button>

          <h3>Tableau de bord</h3>
          {lien('/', 'accueil', 'Accueil', pathname === '/', true)}

          {MODULES.map((module) => {
            const groupes = module.groupes
              .map((groupe) => ({
                ...groupe,
                entrees: groupe.entrees.filter(
                  (e) => !e.roles || e.roles.length === 0 || aRole(...e.roles),
                ),
              }))
              .filter((groupe) => groupe.entrees.length > 0);
            if (groupes.length === 0) return null;

            const ouverte = ouvertes.includes(module.section);
            const idContenu = `rail-${module.section}`;

            return (
              <div key={module.section} className="rail__module">
                <h3>
                  <button
                    type="button"
                    className="rail__section"
                    aria-expanded={ouverte}
                    aria-controls={idContenu}
                    onClick={() => basculerSection(module.section)}
                  >
                    {module.section}
                    <Icone nom="derouler" taille={14} />
                  </button>
                </h3>
                {/* Rail replié : plus de titres à cliquer, tout reste à portée. */}
                <div id={idContenu} hidden={!ouverte && !replie}>
                  {groupes.map((groupe, rang) => (
                    <div key={groupe.titre ?? rang}>
                      {groupe.titre && <h4>{groupe.titre}</h4>}
                      {groupe.entrees.map((entree) =>
                        lien(
                          entree.chemin,
                          entree.icone,
                          entree.libelle,
                          estActif(entree.chemin, pathname, search),
                        ),
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <main className="page">
          <Outlet />
        </main>
      </div>
    </>
  );
}
